import jalaali from 'jalaali-js';


const pad2 = (value) => value.toString().padStart(2, '0');

export const formatGregorianDateForInput = (date = new Date()) => {
  if (!date || Number.isNaN(new Date(date).getTime())) return '';
  const d = new Date(date);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

export const formatGregorianDateTimeForInput = (date = new Date()) => {
  if (!date || Number.isNaN(new Date(date).getTime())) return '';
  const d = new Date(date);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

export const parseGregorianDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const normalized = String(value).trim();
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const [, year, month, day] = match;
  const parsed = new Date(Number(year), Number(month) - 1, Number(day), 12, 0, 0, 0);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const parseGregorianDateTime = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const normalized = String(value).trim();
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (match) {
    const [, year, month, day, hour, minute] = match;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), 0, 0);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Convert Gregorian date to Jalali
 */
export const toJalali = (date) => {
  if (!date) return null;
  
  const d = new Date(date);
  const { jy, jm, jd } = jalaali.toJalaali(d.getFullYear(), d.getMonth() + 1, d.getDate());
  
  return {
    year: jy,
    month: jm,
    day: jd,
  };
};

/**
 * Convert Jalali date to Gregorian
 */
export const toGregorian = (jYear, jMonth, jDay) => {
  const { gy, gm, gd } = jalaali.toGregorian(jYear, jMonth, jDay);
  return new Date(gy, gm - 1, gd);
};

/**
 * Format Jalali date string
 */
export const formatJalaliDate = (date, format = 'YYYY/MM/DD') => {
  if (!date) return '-';
  
  const jalali = toJalali(date);
  if (!jalali) return '-';
  
  const { year, month, day } = jalali;
  
  return format
    .replace('YYYY', year.toString())
    .replace('MM', month.toString().padStart(2, '0'))
    .replace('DD', day.toString().padStart(2, '0'));
};

/**
 * Format datetime to Jalali with time
 */
export const formatJalaliDateTime = (datetime) => {
  if (!datetime) return '-';
  
  const d = new Date(datetime);
  const jalali = toJalali(d);
  
  if (!jalali) return '-';
  
  const hours = d.getHours().toString().padStart(2, '0');
  const minutes = d.getMinutes().toString().padStart(2, '0');
  
  return `${jalali.year}/${jalali.month.toString().padStart(2, '0')}/${jalali.day.toString().padStart(2, '0')} ${hours}:${minutes}`;
};

/**
 * Parse Jalali date string to Date object
 */
export const parseJalaliDate = (jalaliStr) => {
  if (!jalaliStr) return null;
  
  const parts = jalaliStr.split('/').map(Number);
  if (parts.length !== 3) return null;
  
  const [year, month, day] = parts;
  return toGregorian(year, month, day);
};

/**
 * Get today's date in Jalali
 */
export const getTodayJalali = () => {
  return toJalali(new Date());
};

/**
 * Get Jalali month names
 */
export const jalaliMonths = [
  'فروردین', 'اردیبهشت', 'خرداد',
  'تیر', 'مرداد', 'شهریور',
  'مهر', 'آبان', 'آذر',
  'دی', 'بهمن', 'اسفند'
];

/**
 * Get Jalali weekday names
 */
export const jalaliWeekdays = [
  'شنبه', 'یکشنبه', 'دوشنبه', 
  'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه', 'جمعه'
];

/**
 * Format relative date in Persian
 */
export const formatRelativeDate = (date) => {
  if (!date) return '-';
  
  const now = new Date();
  const target = new Date(date);
  const diffTime = target - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'امروز';
  if (diffDays === 1) return 'فردا';
  if (diffDays === -1) return 'دیروز';
  if (diffDays > 0 && diffDays <= 7) return `${diffDays} روز دیگر`;
  if (diffDays < 0 && diffDays >= -7) return `${Math.abs(diffDays)} روز پیش`;
  
  return formatJalaliDate(date);
};

/**
 * Convert numbers to Persian
 */
export const toPersianNumber = (num) => {
  if (num === null || num === undefined) return '';
  
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  return num.toString().replace(/\d/g, (d) => persianDigits[parseInt(d)]);
};

/**
 * Format time string
 */
export const formatTime = (timeStr) => {
  if (!timeStr) return '-';
  
  const parts = timeStr.split(':');
  return `${parts[0]}:${parts[1]}`;
};


const GREGORIAN_DATE_TOKEN_RE = /\b(19\d{2}|20\d{2})[-/.](0[1-9]|1[0-2])[-/.](0[1-9]|[12]\d|3[01])(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?)?\b/g;

const shouldSkipDateNode = (node) => {
  const parent = node?.parentElement;
  if (!parent) return true;
  if (parent.closest('[data-date-guard="off"]')) return true;
  const tag = parent.tagName;
  return ['INPUT', 'TEXTAREA', 'SELECT', 'OPTION', 'SCRIPT', 'STYLE', 'CODE', 'PRE'].includes(tag);
};

export const looksLikeGregorianDateToken = (value) => {
  if (value === null || value === undefined) return false;
  const normalized = String(value).trim();
  return /^(19\d{2}|20\d{2})[-/.](0[1-9]|1[0-2])[-/.](0[1-9]|[12]\d|3[01])(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?)?$/.test(normalized);
};

export const formatAutoJalali = (value) => {
  if (value === null || value === undefined || value === '') return value;
  const normalized = String(value).trim();
  if (!looksLikeGregorianDateToken(normalized)) return value;

  const date = parseGregorianDateTime(normalized) || parseGregorianDate(normalized);
  if (!date) return value;

  return normalized.includes(':') ? formatJalaliDateTime(date) : formatJalaliDate(date);
};

export const replaceGregorianDatesInText = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text.replace(GREGORIAN_DATE_TOKEN_RE, (match) => formatAutoJalali(match));
};

export const enforceJalaliDatesInDom = (root = document?.body) => {
  if (!root || typeof document === 'undefined') return;

  root.querySelectorAll?.('[title], [placeholder], input[readonly], input:disabled').forEach((element) => {
    ['title', 'placeholder', 'value'].forEach((attr) => {
      const current = attr === 'value' ? element.value : element.getAttribute?.(attr);
      if (!current || !looksLikeGregorianDateToken(current)) return;
      const next = formatAutoJalali(current);
      if (!next || next === current) return;
      if (attr === 'value') {
        element.value = next;
      } else {
        element.setAttribute(attr, next);
      }
    });
  });

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node?.textContent || !node.textContent.trim()) return NodeFilter.FILTER_REJECT;
      if (shouldSkipDateNode(node)) return NodeFilter.FILTER_REJECT;
      GREGORIAN_DATE_TOKEN_RE.lastIndex = 0;
      return GREGORIAN_DATE_TOKEN_RE.test(node.textContent)
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT;
    },
  });

  const nodes = [];
  let current = walker.nextNode();
  while (current) {
    nodes.push(current);
    current = walker.nextNode();
  }

  nodes.forEach((node) => {
    const next = replaceGregorianDatesInText(node.textContent);
    if (next !== node.textContent) {
      node.textContent = next;
    }
  });
};

export const getRelativeDayLabel = (date) => formatRelativeDate(date);

export const formatJalaliDateRange = (startDate, endDate, separator = ' تا ') => {
  if (!startDate && !endDate) return '-';
  if (startDate && !endDate) return formatJalaliDate(startDate);
  if (!startDate && endDate) return formatJalaliDate(endDate);
  return `${formatJalaliDate(startDate)}${separator}${formatJalaliDate(endDate)}`;
};

export const getTodayJalaliLabel = () => formatJalaliDate(new Date());
